import type { ChatI18n } from '../types.js';

export const CHAT_I18N_EN: ChatI18n = {
  headerTitle: 'Product Expert',
  inputPlaceholder: 'Search products, ask questions',
  inputAriaLabel: 'Type your message',
  sendButton: 'Send',
  closeButton: 'Close',
  openButton: 'Open chat',
  newChatButton: 'New chat',
  poweredBy: 'Powered by Gengage',
  errorMessage: 'Something went wrong. Please try again.',
  retryButton: 'Retry',
  loadingMessage: 'Reviewing your request...',
  loadingSequenceGeneric: [
    'Reviewing your request...',
    'Reviewing product details...',
    'Checking the strongest signals...',
    'Evaluating what I found...',
    'Reviewing the details...',
  ],
  loadingSequencePanel: [
    'Reviewing the most relevant details',
    'Checking the strongest signals',
    'Preparing this view for you',
  ],
  loadingSequenceComparison: [
    'Reviewing the product differences',
    'Checking the strongest tradeoffs',
    'Preparing your comparison',
  ],
  productCtaLabel: 'Buy',
  viewOnSiteLabel: 'View on Site',
  attachImageButton: 'Attach image',
  attachMenuSelectPhoto: 'Choose photo',
  attachMenuPaste: 'Paste',
  clipboardNoImageMessage: 'No image found in clipboard. Copy an image first or choose a file.',
  removeAttachmentButton: 'Remove image',
  invalidFileType: 'Only JPEG, PNG and WebP files are supported.',
  fileTooLarge: 'File must be smaller than 5 MB.',
  aiTopPicksTitle: 'Top Picks for You',
  roleWinner: 'Top Pick',
  roleBestValue: 'Best Value',
  roleBestAlternative: 'Best Alternative',
  viewDetails: 'View Details',
  groundingReviewCta: 'Read Reviews',
  groundingReviewSubtitle: '{count} reviews available',
  variantsLabel: 'Variants',
  sortRelated: 'Recommended',
  sortPriceAsc: 'Price low to high',
  sortPriceDesc: 'Price high to low',
  sortToolbarAriaLabel: 'Sort products',
  compareSelected: 'Compare',
  productGridSectionCountLabel: '{count} product(s)',
  compareMinHint: 'Select at least 2 products',
  compareMaxHint: 'You can select up to 5 products',
  comparisonSelectLabel: 'Select to compare',
  comparisonSelectedLabel: 'Selected',
  comparisonSelectCardHint: 'Tap anywhere on the card to add or remove it from comparison.',
  comparisonPreparingLabel: 'Preparing your comparison…',
  panelTitleProductDetails: 'Product Details',
  panelTitleSimilarProducts: 'Similar Products',
  panelTitleComparisonResults: 'Comparison Results',
  panelTitleCategories: 'Categories',
  panelTitleSearchResults: 'Search Results',
  inStockLabel: 'In Stock',
  outOfStockLabel: 'Out of Stock',
  findSimilarLabel: 'Find Similar',
  galleryPrevAriaLabel: 'Previous image',
  galleryNextAriaLabel: 'Next image',
  beautyStylesPreparedTitle: 'Prepared {count} recommendation directions for you',
  consultingOtherCompatibleProductsLabel: 'Other compatible products',
  consultingFallbackGroupLabel: 'Recommendation',
  consultingFallbackStyleLabel: 'Style {index}',
  consultingStyleLoadingDescription: 'I am still collecting products for this style. The panel will refresh shortly.',
  consultingStyleUnavailableDescription:
    'I could not find enough product matches for this style yet. You can review the other styles.',
  consultingStyleLoadingBadge: 'Loading',
  consultingStyleUnavailableBadge: 'Not ready',
  choicePrompterHeading: "Can't decide?",
  choicePrompterSuggestion: 'Select products to compare them',
  choicePrompterCta: 'Select & Compare',
  viewMoreLabel: 'Show More',
  similarProductsLabel: 'Similar Products',
  addToCartButton: 'Add to Cart',
  addingToCartToast: 'Adding to cart...',
  addedToCartToast: 'Added to cart',
  shareButton: 'Share',
  productInfoTab: 'Product Info',
  specificationsTab: 'Specifications',
  recommendedChoiceLabel: 'Recommended Choice',
  highlightsLabel: 'Key Highlights',
  keyDifferencesLabel: 'Key Differences',
  specialCasesLabel: 'For Special Cases',
  emptyReviewsMessage: 'No review summary found.',
  closeAriaLabel: 'Close',
  startChatLabel: 'Start Chat',
  voiceButton: 'Voice input',
  voiceListening: 'Listening...',
  voiceNotSupported: 'Voice input is not supported in this browser.',
  voicePermissionDenied: 'Microphone access denied.',
  voiceError: 'Voice input error.',
  handoffHeading: 'Transferring to a support agent',
  productNotFoundMessage: 'Product information is currently unavailable. I can help with something else.',
  stopGenerating: 'Stop generating',
  offlineMessage: "You're offline — messages will send when you reconnect.",
  cartAriaLabel: 'My cart',
  favoritesAriaLabel: 'My favorites',
  historyAriaLabel: 'Chat history',
  historyPageTitle: 'Chat History',
  historyImageAttachmentLabel: 'Image attachment',
  emptyHistoryMessage: "You don't have any saved chats yet.",
  historyDeleteLabel: 'Delete chat',
  historyDeleteConfirmMessage: 'Delete this chat from history? This cannot be undone.',
  showPanelAriaLabel: 'Show Results',
  addToFavoritesLabel: 'Add to favorites',
  customerReviewsTitle: 'Customer Reviews',
  togglePanelAriaLabel: 'Toggle panel',
  chatMessagesAriaLabel: 'Chat messages',
  suggestionsAriaLabel: 'Suggestions',
  previousSuggestionsAriaLabel: 'Previous suggestions',
  moreSuggestionsAriaLabel: 'More suggestions',
  rollbackAriaLabel: 'Rollback to this message',
  backAriaLabel: 'Back',
  forwardAriaLabel: 'Forward',
  closePanelAriaLabel: 'Close panel',
  dismissAriaLabel: 'Dismiss',
  cartAddErrorMessage: "Sorry, I couldn't add that to your cart. Something went wrong.",
  favoriteToggleErrorMessage: "We couldn't update favorites. Please try again.",
  reviewFilterPositive: 'Positive',
  reviewFilterNegative: 'Negative',
  decreaseLabel: 'Decrease',
  increaseLabel: 'Increase',
  reviewCustomersMentionSingular: '1 customer mentions',
  reviewCustomersMentionPlural: 'customers mention',
  reviewSubjectsHeading: 'Select to learn more',
  tryAgainButton: 'Try again',
  askSomethingElseButton: 'Ask something else',
  accountInactiveMessage: 'This account is currently inactive. Please try again later.',
  favoritesPageTitle: 'My Favorites',
  emptyFavoritesMessage: 'No favorites yet. Heart a product to save it here.',
  showFormerMessagesButton: '↑ Show earlier messages',
  aiAnalysisAnalyzingLabel: 'Analyzing products...',
  aiBrowseCategoriesTitle: 'Browse categories',
  photoAnalysisBadge: 'Skin Analysis',
  photoAnalysisStrengthsLabel: 'Your strengths',
  photoAnalysisFocusLabel: 'Focus points',
  photoAnalysisCelebStyleLabel: 'Celeb style match',
  beautyPhotoStepTitle: 'Personalize with a selfie',
  beautyPhotoStepDescription: 'Upload a clear profile photo so I can give you personalized recommendations.',
  beautyPhotoStepUpload: 'Upload Photo',
  beautyPhotoStepProcessing: 'Processing photo...',
  beautyPhotoStepSkip: 'Skip',
  beautyPhotoStepSkipMessage: 'Skip the photo step',
};
